import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { mapWithIndex } from 'fp-ts/Record';
import { omitFalsyValues } from '@mews/fp';
import { Prettify } from '@mews/std';

export type FlagToEnumMap<T extends object, Prefix extends string = ''> = {
    [key in keyof T]-?: key extends `${Prefix}${infer K}` ? Capitalize<K> : never;
};

type FlagEnumValues<T extends string, FlagPrefix extends string> = Prettify<
    FlagToEnumMap<Record<T, boolean>, FlagPrefix>[T][]
>;

const createMenuItemsArray = <T extends string, FlagPrefix extends string>(
    flagToEnumMap: FlagToEnumMap<Record<T, boolean>, FlagPrefix>
): FlagEnumValues<T, FlagPrefix> => Object.values(flagToEnumMap);

const mapFlagsToArray =
    <T extends string, FlagPrefix extends string>(flagToEnumMap: FlagToEnumMap<Record<T, boolean>, FlagPrefix>) =>
    (booleanMap: Record<T, boolean>) =>
        pipe(
            booleanMap,
            omitFalsyValues,
            record.keys<T>,
            array.map((checkFlagName: T) => flagToEnumMap[checkFlagName])
        );

const mapArrayToFlags =
    <T extends string, FlagPrefix extends string>(flagToEnumMap: FlagToEnumMap<Record<T, boolean>, FlagPrefix>) =>
    (selectedValues: FlagEnumValues<T, FlagPrefix> | undefined) => {
        const result = {} as Record<T, boolean>;

        pipe(
            flagToEnumMap,
            mapWithIndex<T, string, boolean>(
                (key, value) => (result[key] = (selectedValues as string[])?.includes(value) ?? false)
            )
        );

        return result;
    };

/** @deprecated Use FlagSelectField with Validation.Flag.Default schema instead */
export const createFlagEnum =
    <EnumPrefix extends string>(enumPrefix: EnumPrefix) =>
    <T extends string, FlagPrefix extends string>(flagToEnumMap: FlagToEnumMap<Record<T, boolean>, FlagPrefix>) => ({
        enumPrefix,
        menuItems: createMenuItemsArray(flagToEnumMap),
        mapFlagsToArray: mapFlagsToArray(flagToEnumMap),
        mapArrayToFlags: mapArrayToFlags(flagToEnumMap),
        flagToEnumMap,
    });
